import type { Dispatch, SetStateAction } from "react";
import type {
  NewProgram,
  Plan,
  PlanStarter,
  Program,
  ProgramModel,
} from "@heraion/shared";
import {
  LibraryBrowser,
  useCatalogItems,
  type LibraryFilter,
  type LibraryTagFilters,
  type UiTheme,
} from "@heraion/ui";

import { apiFetch } from "./api";
import { exerciseCatalog } from "./logger/exercisesCache";
import { durableWrite } from "./durableWrite";
import { useLoggerProfile } from "./logger/useLoggerProfile";
import { ProfileLoadState } from "./ProfileLoadState";
import { bodyweightOf, useProfileUnitOrNull, useShellProfile } from "./ThemeProfileBridge";
import { useWorkoutStarter } from "./useWorkoutStarter";

// Web mount of the shared <LibraryBrowser> — mobile CreateHubLibrary's twin.
// Seams: the web apiFetch and the shared catalog hook. onStarter hands a
// built PlanStarter up to useCreateHubWeb, which routes it to the Workouts
// tab the same way PlanScreen's own "Start" does (RW-18 follow-up). No
// working RN Alert → delete confirms inline (two-tap) on the card. Mounts
// only while the hub is open (Modal unmounts hidden children), so this IS
// the lazy load, same as mobile.
export function CreateHubLibraryWeb({
  theme,
  dark,
  callerSub,
  authoredAs = null,
  athleteRoster,
  onEditProgram,
  onEditPlan,
  onPlanFromModel,
  onEditFromModel,
  onStarter,
  filter,
  onFilterChange,
  tagFilters,
  onTagFiltersChange,
  notify,
}: {
  theme: UiTheme;
  dark: boolean;
  // AppShell always resolves `user.sub` before mounting this hub (never
  // null in practice), but the type stays `string | null` for coherence
  // with LibraryBrowser's own callerSub — see useLibrary.ts's header.
  callerSub: string | null;
  /** CO-19 — straight pass-through to LibraryBrowser/useLibrary's own
   *  authoredAs (same "not yet known" `null` default). useCreateHubWeb
   *  supplies 'coach' when the hub was opened from the coach view; every
   *  other caller keeps the unfiltered default. */
  authoredAs?: Program["authoredAs"] | null;
  /** CO-21 — AssignSheet's roster, straight pass-through to LibraryBrowser
   *  (same "not yet known" `undefined` default). AppShell's useCoachStatus
   *  is the one read (CN-coach-edges-fetch); only meaningful while
   *  authoredAs === 'coach', harmless otherwise (Assign icon never renders
   *  for an athlete-authored row). */
  athleteRoster?: ReadonlyArray<string>;
  onEditProgram: (program: Program) => void;
  onEditPlan: (plan: Plan) => void;
  onPlanFromModel: (model: Omit<ProgramModel, "id">) => void;
  /** #472 F3: the catalog widget's Edit: close the hub, open the editor on it. */
  onEditFromModel: (seed: NewProgram) => void;
  onStarter: (starter: PlanStarter) => void;
  /** MX-8b: the hub's own search, lifted into useCreateHubWeb so a
   *  wide/narrow resize while the hub is open does not reset it. Omit for
   *  LibraryBrowser's own uncontrolled state. */
  filter?: LibraryFilter;
  onFilterChange?: (f: LibraryFilter) => void;
  tagFilters?: LibraryTagFilters;
  onTagFiltersChange?: Dispatch<SetStateAction<LibraryTagFilters>>;
  /** #472: the hub's toast (useCreateHubWeb), outside the hub's scroll. */
  notify?: (message: string) => void;
}) {
  const catalog = useCatalogItems({ catalog: exerciseCatalog });
  // The plan previews show prescribed loads, which are stored in kg. #343:
  // «Save as model» writes its load step in this unit, so no list before it.
  const { doc } = useShellProfile();
  const units = useProfileUnitOrNull();
  // #472: following a model writes its effort in the athlete's scale.
  const loggerProfile = useLoggerProfile();
  const bodyweightKg = bodyweightOf(doc);
  const { starting, startPlan, maxMeta } = useWorkoutStarter(
    catalog.raw ? [...catalog.raw] : null,
    bodyweightKg,
    onStarter,
  );
  if (units === null || loggerProfile === null) return <ProfileLoadState theme={theme} />;
  return (
    <LibraryBrowser
      theme={theme}
      dark={dark}
      apiFetch={apiFetch}
      durableWrite={durableWrite}
      catalog={catalog.raw ? [...catalog.raw] : null}
      unit={units}
      maxMeta={maxMeta}
      effortScale={loggerProfile.effortScale}
      callerSub={callerSub}
      authoredAs={authoredAs}
      athleteRoster={athleteRoster}
      onEditProgram={onEditProgram}
      onEditPlan={onEditPlan}
      onPlanFromModel={onPlanFromModel}
      onEditFromModel={onEditFromModel}
      onStartPlan={(plan) => void startPlan(plan)}
      startingPlanId={starting}
      filter={filter}
      onFilterChange={onFilterChange}
      tagFilters={tagFilters}
      onTagFiltersChange={onTagFiltersChange}
      notify={notify}
    />
  );
}
